import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Typography,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { createSelectionModel } from '../../../utils/dataGridHelpers';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  InfoOutlined as InfoIcon,
  CheckCircle,
  Print,
  Warning,
  CloudDownload as ImportIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { panelNeutral } from '../../../theme/gridSurfaces';
import { usePharmacyStore } from '../../../stores/pharmacyStore';
import {
  KrollNote,
  KrollNoteCategory,
  KrollNotesResponse,
  KrollCategoriesResponse,
} from './types';

interface KrollNotesPanelProps {
  patientId: string; // Composite ID: pharmacyId_patientNumber
  /** Called after notes are successfully imported into the internal notes system. */
  onImported?: () => void;
}

/**
 * KrollNotesPanel - Display read-only patient notes from Kroll PatCom table
 *
 * Features:
 * - Loads notes from Kroll via MQTT
 * - Dynamic category filtering from Kroll Lists table
 * - Client-side search and filtering
 * - Read-only display (no editing)
 * - DataGrid with sorting and pagination
 */
export const KrollNotesPanel: React.FC<KrollNotesPanelProps> = ({ patientId, onImported }) => {
  const activePharmacy = usePharmacyStore((state: any) => state.activePharmacy);
  const { enqueueSnackbar } = useSnackbar();

  const [notes, setNotes] = useState<KrollNote[]>([]);
  const [categories, setCategories] = useState<KrollNoteCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNoteIds, setSelectedNoteIds] = useState<GridRowSelectionModel>(createSelectionModel());
  const [viewNoteId, setViewNoteId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [notesToImport, setNotesToImport] = useState<KrollNote[]>([]);
  const [importCategory, setImportCategory] = useState<string>('general');

  // Fetch categories on mount (once per pharmacy)
  useEffect(() => {
    if (!activePharmacy?.id) return;

    const fetchCategories = async () => {
      try {
        const response = await api.get<KrollCategoriesResponse>(
          `/api/kroll-notes/categories/${activePharmacy.id}`
        );

        if (response.data.success) {
          setCategories(response.data.categories);
        }
      } catch (err: any) {
        console.error('Failed to fetch Kroll note categories:', err);
        // Don't show error - categories are optional
      }
    };

    fetchCategories();
  }, [activePharmacy?.id]);

  // Fetch notes when patient changes
  useEffect(() => {
    if (!patientId) {
      setNotes([]);
      setLoading(false);
      return;
    }

    fetchNotes();
  }, [patientId]);

  const fetchNotes = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<KrollNotesResponse>(
        `/api/kroll-notes/patient/${patientId}`
      );

      if (response.data.success) {
        setNotes(response.data.notes);
      } else {
        throw new Error('Failed to fetch Kroll notes');
      }
    } catch (err: any) {
      console.error('Error fetching Kroll notes:', err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to load pharmacy-system notes';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter notes based on search query and selected categories
  const filteredNotes = useMemo(() => {
    return notes.filter((note) => {
      // Search filter (content + topic)
      const matchesSearch =
        !searchQuery ||
        note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.topic.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.includes(note.topic);

      return matchesSearch && matchesCategory;
    });
  }, [notes, searchQuery, selectedCategories]);

  // Resolve the actual selected notes from the MUI X v8 selection model.
  // The model is { type: 'include' | 'exclude', ids: Set }. Clicking the header
  // "select all" checkbox yields type:'exclude' with an EMPTY ids set (nothing
  // excluded ⇒ everything selected) — so counting model.ids directly reported 0
  // and the Import button vanished on select-all. Resolve against the currently
  // visible rows so both include and exclude selections are counted correctly.
  const selectedNotes = useMemo(() => {
    const model = selectedNoteIds as { type?: 'include' | 'exclude'; ids?: Set<unknown> };
    const ids = model?.ids ?? new Set<unknown>();
    const isExclude = model?.type === 'exclude';
    return filteredNotes.filter((note) => {
      const inSet = ids.has(note.id) || ids.has(String(note.id));
      return isExclude ? !inSet : inSet;
    });
  }, [selectedNoteIds, filteredNotes]);
  const selectedCount = selectedNotes.length;

  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
  };

  const handleRefresh = () => {
    fetchNotes();
  };

  const handleImportClick = () => {
    if (selectedNotes.length === 0) {
      return;
    }
    setNotesToImport(selectedNotes);
    setConfirmImportOpen(true);
  };

  const handleConfirmImport = async () => {
    setConfirmImportOpen(false);
    setImporting(true);

    try {
      const response = await api.post(`/api/kroll-notes/import/${patientId}`, {
        notes: notesToImport,
        category: importCategory,
      });

      if (response.data.success) {
        // GP-377: the backend skips notes that were already imported.
        const skipped = response.data.skipped || 0;
        enqueueSnackbar(
          `Imported ${response.data.imported} note${response.data.imported !== 1 ? 's' : ''} to the notes system` +
            (skipped > 0 ? ` (${skipped} already imported — skipped)` : ''),
          { variant: skipped > 0 && response.data.imported === 0 ? 'info' : 'success' },
        );
        setSelectedNoteIds(createSelectionModel());
        setNotesToImport([]);
        onImported?.();
      } else {
        throw new Error(response.data.message || 'Import failed');
      }
    } catch (err: any) {
      console.error('Failed to import notes:', err);
      enqueueSnackbar(
        err.response?.data?.message || err.message || 'Failed to import notes',
        { variant: 'error' },
      );
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setConfirmImportOpen(false);
    setNotesToImport([]);
  };

  const viewNote = useMemo(() => {
    return notes.find((note) => note.id === viewNoteId);
  }, [notes, viewNoteId]);

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={() => setViewNoteId(params.row.id)}
          title="View full note"
        >
          <ViewIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: 'changed',
      headerName: 'Last Modified',
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.changed) return '-';
        const date = new Date(params.row.changed);
        return (
          <Tooltip title={format(date, 'PPpp')}>
            <span>{format(date, 'MMM dd, yyyy')}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'topic',
      headerName: 'Category',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'General'}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'content',
      headerName: 'Note Content',
      flex: 1,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} placement="top-start">
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'flags',
      headerName: 'Flags',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {params.row.showOnRx && (
            <Tooltip title="Show on Rx">
              <Chip label="Rx" size="small" icon={<CheckCircle />} color="success" />
            </Tooltip>
          )}
          {params.row.printOnHardcopy && (
            <Tooltip title="Print on hardcopy">
              <Chip label="Print" size="small" icon={<Print />} color="info" />
            </Tooltip>
          )}
          {params.row.conspicuous && (
            <Tooltip title="Conspicuous/Important">
              <Chip label="!" size="small" icon={<Warning />} color="warning" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.created) return '-';
        const date = new Date(params.row.created);
        return (
          <Tooltip title={format(date, 'PPpp')}>
            <span>{format(date, 'MMM dd, yyyy')}</span>
          </Tooltip>
        );
      },
    },
  ];

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" action={
        <Button onClick={handleRefresh} color="inherit" size="small">
          Retry
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Read-only info banner */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="body2">
          These notes are read-only data from the Kroll pharmacy system. To add or edit notes, please use the "Manage Notes" tab.
        </Typography>
      </Alert>

      {/* Filter controls */}
      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search box */}
          <TextField
            size="small"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Category filter (only show if categories exist) */}
          {categories.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                multiple
                value={selectedCategories}
                onChange={handleCategoryChange}
                label="Filter by Category"
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All Categories'
                    : `${selected.length} selected`
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category.displayValue} value={category.displayValue}>
                    {category.displayValue}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Clear filters button */}
          {(searchQuery || selectedCategories.length > 0) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          )}

          {/* Refresh button */}
          <Tooltip title="Refresh notes">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Active filters display */}
        {(searchQuery || selectedCategories.length > 0) && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                onDelete={() => setSearchQuery('')}
                size="small"
              />
            )}
            {selectedCategories.map((category) => (
              <Chip
                key={category}
                label={`Category: ${category}`}
                onDelete={() =>
                  setSelectedCategories(selectedCategories.filter((c) => c !== category))
                }
                size="small"
                color="primary"
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Results summary and Import button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredNotes.length} of {notes.length} note{notes.length !== 1 ? 's' : ''}
          {selectedCount > 0 && ` • ${selectedCount} selected`}
        </Typography>
        {selectedCount > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<ImportIcon />}
            onClick={handleImportClick}
            disabled={importing}
          >
            {importing ? 'Importing...' : `Import ${selectedCount} Note${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        )}
      </Box>

      {/* DataGrid */}
      {filteredNotes.length === 0 ? (
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          {notes.length === 0
            ? 'No pharmacy-system notes found for this patient.'
            : 'No notes match your current filters.'}
        </Alert>
      ) : (
          // Wrap the grid in a flex-sized box and let the grid fill it with
          // height:100%, rather than making the DataGrid itself the flex item.
          // As a direct flex child the grid's internal ResizeObserver fights the
          // flex measurement on every scroll tick → the "stutter" while scrolling.
          // A definite parent height makes scrolling smooth. (`autoHeight` was
          // removed first because it overflowed the clipped container.)
          <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <DataGrid
              rows={filteredNotes}
              columns={columns}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              checkboxSelection
              rowSelectionModel={selectedNoteIds}
              onRowSelectionModelChange={setSelectedNoteIds}
              sx={{
                height: '100%',
                border: 1,
                borderColor: 'divider',
                // Keep the mouse wheel inside the grid's own scroller. Without
                // this, when the grid hits a scroll boundary the wheel chains up
                // to the Patient 360 tab's outer overflow:auto container, and the
                // two scroll areas fight each other → the stutter on wheel scroll.
                '& .MuiDataGrid-virtualScroller': {
                  overscrollBehavior: 'contain',
                },
                '& .MuiDataGrid-cell': {
                  py: 1,
                },
              }}
            />
          </Box>
      )}

      {/* Confirmation Dialog for Import */}
      <Dialog
        open={confirmImportOpen}
        onClose={handleCancelImport}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Import</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to import {notesToImport.length} note{notesToImport.length !== 1 ? 's' : ''} into the internal notes system?
          </Typography>

          {/* Category Selector */}
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel id="import-category-label">Import to Category</InputLabel>
            <Select
              labelId="import-category-label"
              value={importCategory}
              label="Import to Category"
              onChange={(e) => setImportCategory(e.target.value)}
            >
              <MenuItem value="clinical">Clinical</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="delivery">Delivery</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The notes will be tagged as "migrated-from-kroll" and will appear in the Manage Notes tab under the selected category.
            Kroll metadata (created/modified dates, flags) will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImport} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            color="primary"
            startIcon={<ImportIcon />}
          >
            Confirm Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Note Dialog */}
      <Dialog
        open={!!viewNoteId}
        onClose={() => setViewNoteId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          View Kroll Note
          {viewNote && (
            <Chip
              label={viewNote.topic || 'General'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {viewNote && (
            <Box>
              {/* Note Metadata */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Created:</strong> {format(new Date(viewNote.created), 'PPpp')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>Modified:</strong> {format(new Date(viewNote.changed), 'PPpp')}
                </Typography>
              </Box>

              {/* Flags */}
              {(viewNote.showOnRx || viewNote.printOnHardcopy || viewNote.conspicuous) && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {viewNote.showOnRx && (
                    <Chip label="Show on Rx" size="small" icon={<CheckCircle />} color="success" />
                  )}
                  {viewNote.printOnHardcopy && (
                    <Chip label="Print on Hardcopy" size="small" icon={<Print />} color="info" />
                  )}
                  {viewNote.conspicuous && (
                    <Chip label="Conspicuous/Important" size="small" icon={<Warning />} color="warning" />
                  )}
                </Box>
              )}

              {/* Note Content */}
              {/* GP-1232: hard-coded #f5f5f5 rendered the note body as a
                  white slab under dark-mode light text. */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: panelNeutral }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {viewNote.content}
                </Typography>
              </Paper>

              {/* Kroll Note ID */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Kroll Note ID: {viewNote.id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewNoteId(null)}>Close</Button>
          {viewNote && (
            <Button
              variant="contained"
              onClick={() => {
                setNotesToImport([viewNote]);
                setViewNoteId(null);
                setConfirmImportOpen(true);
              }}
              disabled={importing}
              startIcon={<ImportIcon />}
            >
              Import This Note
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
